import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { IndustrySearchRequest } from '../types';

import { Industry } from '@/bundles/model';
import { PaginationModel } from '@/components/Table';
import { IS_PRODUCTION_MODE } from '@/config';
import { ExtractState, Params } from '@/types';

interface IndustryStore {
  industries: Industry[];
  searchQuery: IndustrySearchRequest;
  pagination: PaginationModel;
  actions: {
    updateIndustries: (industries: Industry[]) => void;
    updatePagination: (pagination: PaginationModel) => void;
    addSearchQuery: (searchQuery: IndustrySearchRequest) => void;
    resetState: () => void;
  };
}

const defaultPagination = {
  page: 1,
  pageSize: 10,
};

const industryStore = createStore<IndustryStore>()(
  devtools(
    set => ({
      industries: [],
      searchQuery: {},
      pagination: defaultPagination,
      actions: {
        updateIndustries: (industries: Industry[]) => {
          set({
            industries,
          });
        },
        updatePagination: (pagination: PaginationModel) => {
          set({
            pagination,
          });
        },
        addSearchQuery: (searchQuery: IndustrySearchRequest) => {
          set({
            searchQuery,
          });
        },
        resetState: () => {
          set({
            industries: [],
            searchQuery: {},
            pagination: defaultPagination,
          });
        },
      },
    }),
    {
      name: 'industry',
      enable: !IS_PRODUCTION_MODE,
    },
  ),
);

const useIndustryStore = <U>(
  selector: Params<U, typeof industryStore>[1],
  equalityFn?: Params<U, typeof industryStore>[2],
) => {
  return useStoreWithEqualityFn(industryStore, selector, equalityFn);
};

// Selectors
const industriesSelector = (state: ExtractState<typeof industryStore>) =>
  state.industries;

const paginationSelector = (state: ExtractState<typeof industryStore>) =>
  state.pagination;

const searchQuerySelector = (state: ExtractState<typeof industryStore>) =>
  state.searchQuery;

const actionsSelector = (state: ExtractState<typeof industryStore>) =>
  state.actions;

// Getters
const getIndustries = () => industriesSelector(industryStore.getState());

const getPagination = () => paginationSelector(industryStore.getState());

const getSearchQuery = () => searchQuerySelector(industryStore.getState());

const getActions = () => actionsSelector(industryStore.getState());

// Hooks
const useIndustries = () => useIndustryStore(industriesSelector, shallow);
const usePagination = () => useIndustryStore(paginationSelector, shallow);

export {
  defaultPagination,

  // Getters
  getActions,
  getIndustries,
  getPagination,
  getSearchQuery,

  // Hooks
  useIndustries,
  usePagination,
};
